// CONTRACT:C4-DESIGN-TOKENS.1.0
/** The semantic slot taxonomy: the complete, typed set of semantic token keys that EVERY
 *  loadable theme MUST define a value for. This is the stable contract surface — components
 *  and capabilities consume these keys (as CSS variables) only, never raw primitive values.
 *
 *  A slot key is the CSS-variable name without the leading `--`, e.g. "color-surface-base"
 *  emits `--color-surface-base`. The DesignTokens interface (from CONTRACT:C4) groups these
 *  by category; this flat list is the validation/emission source of truth and aligns 1:1
 *  with the grouped shape (see buildDesignTokens in tokens.ts). */

import type { SlotType } from "./errors.ts";

/** A semantic slot declaration: its key (CSS-var name minus `--`) and its declared value type. */
export interface SlotDef {
  /** CSS-variable name without the leading `--`. */
  readonly key: string;
  /** Declared type — a value of the wrong type is rejected (ErrTokenTypeMismatch). */
  readonly type: SlotType;
}

/** The five MIL-STD-2525 / APP-6 affiliation slots — the SINGLE source of affiliation color.
 *  Keys map 1:1 to CONTRACT:P1-CANONICAL-ENVELOPE.1.0 properties.affiliation
 *  (note: P1 uses "friend"; the token uses "friendly" — see affiliationToStatus in status.ts). */
export const STATUS_KEYS = [
  "status-hostile",
  "status-friendly",
  "status-neutral",
  "status-unknown",
  "status-pending",
] as const;

export type StatusKey = (typeof STATUS_KEYS)[number];

/** Color slots: surfaces, interactive states, semantic verdict hues, and the affiliation set. */
const COLOR_SLOTS: readonly SlotDef[] = [
  { key: "color-surface-base", type: "color" },
  { key: "color-surface-raised", type: "color" },
  { key: "color-surface-overlay", type: "color" },
  { key: "color-surface-sunken", type: "color" },
  { key: "color-interactive-default", type: "color" },
  { key: "color-interactive-hover", type: "color" },
  { key: "color-interactive-active", type: "color" },
  { key: "color-interactive-disabled", type: "color" },
  // Semantic verdict/state hues — POLICY-OUTCOME chrome (KAS verdict pill, Trust modal,
  // census RELEASED/REDUCED, staleness). Distinct from affiliation hue (status-*) and from
  // the teal secure spine. Canon: Grant #46D98E / Deny #E5484D / Caution #F2C14E
  // (tak-design-system Design System.dc.html §01 "Semantic"). Never used on a map marker.
  { key: "color-semantic-grant", type: "color" },
  { key: "color-semantic-deny", type: "color" },
  { key: "color-semantic-caution", type: "color" },
  ...STATUS_KEYS.map((key): SlotDef => ({ key, type: "color" })),
];

/** Ambient CHROME slots — the neutral text ramp + hairline rules the shell draws ON TOP of
 *  the themed surfaces (the legible text/border layer over any surface, NOT affiliation color —
 *  that stays status-*). These were hardcoded dark in the COP shell :root; promoting them to
 *  slots makes them theme-controlled so a LIGHT theme can re-point them (dark text on light).
 *  NOTE: `--ui-accent` is intentionally NOT a slot — it derives from `color-interactive-hover`
 *  in the shell :root, so it is already theme-controlled by the interactive slot it references. */
const CHROME_SLOTS: readonly SlotDef[] = [
  { key: "ui-text", type: "color" },
  { key: "ui-text-dim", type: "color" },
  { key: "ui-text-faint", type: "color" },
  { key: "ui-hairline", type: "color" },
  { key: "ui-hairline-soft", type: "color" },
];

/** Corner-radius scale (tak-design-system Design System.dc.html §06 "Radius & elevation"):
 *  tight technical radii — chip (4px) / control (7px) / panel (12px). Theme-invariant. */
const RADIUS_SLOTS: readonly SlotDef[] = [
  { key: "radius-chip", type: "length" },
  { key: "radius-control", type: "length" },
  { key: "radius-panel", type: "length" },
];

/** Typography slots: a small bespoke tactical scale + font families. */
const TYPOGRAPHY_SLOTS: readonly SlotDef[] = [
  { key: "typography-scale-xs", type: "fontSize" },
  { key: "typography-scale-sm", type: "fontSize" },
  { key: "typography-scale-md", type: "fontSize" },
  { key: "typography-scale-lg", type: "fontSize" },
  { key: "typography-scale-xl", type: "fontSize" },
  { key: "typography-family-sans", type: "fontFamily" },
  { key: "typography-family-mono", type: "fontFamily" },
  // The canon's third family slot: a HUD / display face (Saira) for HUD chrome,
  // labels, and numerics — distinct from the sans prose/UI face and the mono
  // envelope/coordinate face (tak-design-system Design System.dc.html §03).
  { key: "typography-family-hud", type: "fontFamily" },
];

/** Spacing scale (4px base, tactical-dense). */
const SPACING_SLOTS: readonly SlotDef[] = [
  { key: "spacing-xs", type: "length" },
  { key: "spacing-sm", type: "length" },
  { key: "spacing-md", type: "length" },
  { key: "spacing-lg", type: "length" },
  { key: "spacing-xl", type: "length" },
];

/** Elevation steps (shadow stacks expressed as a single CSS box-shadow value). */
const ELEVATION_SLOTS: readonly SlotDef[] = [
  { key: "elevation-flat", type: "length" },
  { key: "elevation-raised", type: "length" },
  { key: "elevation-overlay", type: "length" },
];

/** Motion durations + easings. */
const MOTION_SLOTS: readonly SlotDef[] = [
  { key: "motion-duration-instant", type: "duration" },
  { key: "motion-duration-fast", type: "duration" },
  { key: "motion-duration-slow", type: "duration" },
  { key: "motion-easing-standard", type: "easing" },
  { key: "motion-easing-decelerate", type: "easing" },
];

/** The complete ordered slot taxonomy. A loadable theme MUST define every one of these. */
export const SLOTS: readonly SlotDef[] = [
  ...COLOR_SLOTS,
  ...CHROME_SLOTS,
  ...TYPOGRAPHY_SLOTS,
  ...SPACING_SLOTS,
  ...RADIUS_SLOTS,
  ...ELEVATION_SLOTS,
  ...MOTION_SLOTS,
];

/** Index of slot key → declared type, for O(1) validation/type checks. */
export const SLOT_TYPES: Readonly<Record<string, SlotType>> = Object.freeze(
  Object.fromEntries(SLOTS.map((s) => [s.key, s.type])),
);

/** All slot keys (the full semantic vocabulary). */
export const SLOT_KEYS: readonly string[] = SLOTS.map((s) => s.key);
